import React, { useState } from "react";
import { Document, Page, Image, StyleSheet, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import Head from "next/head";
import { FaFileUpload } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import { FaDownload } from "react-icons/fa";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

import UploadFile from "@components/PdfTools/UploadFile";
import Steps from "@components/PdfTools/Steps";
import WhyChooseBolosign from "@components/PdfTools/WhyChooseBolosign";
import PricingCards from "@components/PdfTools/PricingCards";
import TryBolosign from "@components/PdfTools/TryBolosign";
import OtherPdfTools from "@components/PdfTools/OtherPdfTools";
import FaqCard from "@components/Cards/FaqCard";
import { LuUpload } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import { GrDocumentPdf } from "react-icons/gr";

const steps = [
  {
    icon: <LuUpload />,
    title: "Upload Your PNG",
    description:
      "Drag and drop your PNG file into the area above or click 'Select File' to choose the file from your device. Our platform supports all the conversions from PNG to PDF, making your tasks simpler.",
  },
  {
    icon: <GrDocumentPdf />,
    title: "Redefine Your PDF",
    description:
      "Use our customizable settings to adjust the output of your PDF. Need to change a PNG to a PDF? No problem! Adjust image quality, orientation, and margins to fit your needs.",
  },
  {
    icon: <LuDownload />,
    title: "Download Your PDF",
    description:
      "After converting your PNG into a PDF, click 'Download' to save your new PDF file. It's that simple! Your new PDF will be ready to use for any personal or professional purpose.",
  },
];

const reasons = [
  {
    image: "no_signup_required.png",
    title: "No Signup Required",
    description:
      "Convert your files without the need to create an account. Start your PNG to PDF conversion right away.",
  },
  {
    image: "three_quick_steps.png",
    title: "Three Quick Steps",
    description:
      'All you need to do is upload your file, make any desired adjustments, and click "Apply and download" to merge PNGs into a PDF.',
  },
  {
    image: "cloud_hosted.png",
    title: "Cloud-Hosted",
    description:
      "Our tools are cloud-hosted, ensuring you have access anytime and anywhere without using up your device's resources.",
  },
  {
    image: "file_deletion.png",
    title: "File Deletion Policy",
    description:
      "We prioritize your privacy. All uploaded files are automatically deleted after conversion.",
  },
];

const faqData = [
  {
    faqQuestion: "How do I convert a PNG to a PDF?",
    faqAnswer:
      "To convert a PNG to PDF, simply upload your PNG file using our tool. Adjust the settings as needed, and then download your converted PDF. Our tool supports all variations of PNG to PDF conversions, providing a seamless experience.",
  },
  {
    faqQuestion: "Can I convert multiple PNG files to PDF at once?",
    faqAnswer:
      "Yes, our tool allows you to convert multiple PNG files to PDF simultaneously. Just select all the PNGs you want to convert, and our system will combine them into a single PDF document or create individual PDF files based on your preference.",
  },
  {
    faqQuestion: "What is the best way to convert from PNG to PDF?",
    faqAnswer:
      "The best way to convert from PNG to PDF is to use a reliable online converter like BoloSign that maintains the quality of your original image while ensuring the PDF output meets your needs. Adjustments such as image quality and document orientation can be made directly within our tool.",
  },
];

const PAGE_SIZES = [
  "4A0",
  "2A0",
  "A0",
  "A1",
  "A2",
  "A3",
  "A4",
  "A5",
  "A6",
  "A7",
  "A8",
  "A9",
  "A10",
  "B0",
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
  "B6",
  "B7",
  "B8",
  "B9",
  "B10",
  "C0",
  "C1",
  "C2",
  "C3",
  "C4",
  "C5",
  "C6",
  "C7",
  "C8",
  "C9",
  "C10",
  "RA0",
  "RA1",
  "RA2",
  "RA3",
  "RA4",
  "SRA0",
  "SRA1",
  "SRA2",
  "SRA3",
  "SRA4",
  "EXECUTIVE",
  "FOLIO",
  "LEGAL",
  "LETTER",
  "TABLOID",
  "ID1",
];

const DocumentPdf = ({
  images = [],
  pageSize,
  orientation,
  imageAlignment,
}) => {
  const pdfStyles = StyleSheet.create({
    viewer: {
      width: "1000px",
      height: "1000px",
    },
    page: {
      backgroundColor: "#ffffff",
      color: "#000000",
      display: "flex",
      justifyContent: imageAlignment,
      alignItems: "center",
    },
    image: {
      width: "100%",
      height: "auto",
    },
  });

  return (
    <Document>
      {images.map((image, index) => (
        <Page
          key={index}
          size={pageSize}
          style={pdfStyles.page}
          orientation={orientation}
          wrap
        >
          <Image src={image} style={pdfStyles.image} alt={``} />
        </Page>
      ))}
    </Document>
  );
};

const PngToPdf = () => {
  const [images, setImages] = useState([]);
  const [loader, setLoader] = useState(false);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageSize, setPageSize] = useState("A4");
  const [orientation, setOrientation] = useState("portrait");
  const [imageAlignment, setImageAlignment] = useState("center");
  const [signaturePopup, setSignaturePopup] = useState(false);

  const scrollToPreview = () => {
    const element = document.querySelector(".preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleImageUpload(files);
    scrollToPreview();
  };

  const handleUpload = (e) => {
    const files = e.target.files;
    handleImageUpload(files);
    if (files) {
      scrollToPreview();
    }
  };

  const handleFileUpload = (files) => {
    handleImageUpload(files);
    scrollToPreview();
  };

  const handleImageUpload = (files) => {
    // const files = event.target.files;
    setPageRendering(true);
    const imageArray = [];

    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      reader.onloadend = () => {
        imageArray.push(reader.result);
        if (imageArray.length === files.length) {
          setImages([...images, ...imageArray]);
        }
      };
      reader.readAsDataURL(files[i]);
    }
    setPageRendering(false);
  };

  const handleDownload = async () => {
    setLoader(true);
    const doc = (
      <DocumentPdf
        images={images}
        pageSize={pageSize}
        orientation={orientation}
        imageAlignment={imageAlignment}
      />
    );
    const asPdf = pdf([]);
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    saveAs(blob, "document.pdf");
    setLoader(false);
    setTimeout(() => {
      setSignaturePopup(true);
    }, 2000);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>PNG to PDF Tool | BoloForms</title>
        <meta
          name="description"
          content="Convert PNG images to PDF documents online for free. No software installation required."
        />
        <meta
          name="keywords"
          content="png to pdf, png to pdf converter, convert png to pdf, png to pdf online, png to pdf free, png to pdf tool, png to pdf conversion, png to pdf converter online, png to pdf converter free, png to pdf converter tool, image to pdf, image to pdf converter, convert image to pdf"
        />
        <meta name="author" content="BoloForms" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/png-to-pdf"
        />
        <meta property="og:title" content="PNG to PDF Tool | BoloForms" />
        <meta
          property="og:description"
          content="Convert PNG images to PDF documents online for free. No software installation required."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/png-to-pdf"
        />
        <meta property="og:site_name" content="BoloForms" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="BoloForms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BoloForms" />
        <meta name="twitter:creator" content="@BoloForms" />
        <meta name="twitter:title" content="PNG to PDF Tool | BoloForms" />
        <meta
          name="twitter:description"
          content="Convert PNG images to PDF documents online for free. No software installation required."
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/png-to-pdf/"
        />
      </Head>

      <div className="w-full">
        <UploadFile
          heading={"Convert Your PNG to PDF Effortlessly with BoloSign"}
          description={`Do you need to convert PNG to PDF online? Whether for business or personal use, our BoloSign converter allows you to seamlessly transform your PNG files into PDFs. Get started now and convert your images to PDF in a few easy steps. No software installation is needed—access the tool directly from your browser.`}
          bgColor={"bg-[#d5f6d5]"}
          btnColor={"bg-[#b8f6b7]"}
          image={"png-to-pdf.png"}
          handleFileUpload={handleFileUpload}
          acceptFiles="image/*"
        />
        <span className="preview-pdfs"></span>
        <div className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center mt-12 sm:mt-20 px-4 sm:px-8">
          {images.length > 0 && (
            <>
              <h1 className="text-3xl font-light text-center text-gray-700">
                Remove the image which you {`don't`} want
              </h1>
              <div className="">
                <button
                  onClick={() => setImages([])}
                  className="bg-violet-600 mx-auto w-full sm:w-[190px] py-2 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                >
                  Remove All
                </button>
              </div>
              <div className="relative w-full h-[400px] flex flex-wrap justify-center gap-4 border-2 md:px-10 py-4 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="border w-fit relative flex justify-center items-center flex-wrap"
                  >
                    <div className="p-4 bg-white w-[250px]">
                      <img
                        id="image-generated"
                        src={image}
                        alt="pdfImage"
                        className="w-full"
                      />
                    </div>
                    <div className="absolute top-2 left-2 w-8 h-8 bg-black rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                      {index + 1}
                    </div>
                    <div
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 w-8 h-8 bg-gray-400 rounded-full flex justify-center items-center cursor-pointer"
                    >
                      <AiOutlineClose className="text-white text-xl" />
                    </div>
                  </div>
                ))}
                <div
                  id="page-loader"
                  className={`${
                    pageRendering
                      ? "absolute w-full top-0 left-0 h-full flex justify-center items-center bg-white z-50"
                      : "hidden"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    class="w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-violet-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </div>
              </div>
            </>
          )}
          {images.length > 0 && (
            <div id="split-pdfs" className="w-full">
              <div className="mb-10 flex flex-col justify-center items-center gap-4">
                <div className="flex justify-center items-center gap-4 flex-wrap">
                  <div className="">
                    <span className="text-gray-600 text-lg">Page Size : </span>
                    <select
                      onChange={(e) => setPageSize(e.target.value)}
                      className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                      value={pageSize}
                      name=""
                      id=""
                    >
                      {PAGE_SIZES.map((size, index) => (
                        <option key={index} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="">
                    <span className="text-gray-600 text-lg">
                      Orientation :{" "}
                    </span>
                    <select
                      onChange={(e) => setOrientation(e.target.value)}
                      className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                      value={orientation}
                      name=""
                      id=""
                    >
                      <option value="portrait">Portrait</option>
                      <option value="landscape">Landscape</option>
                    </select>
                  </div>
                  <div className="">
                    <span className="text-gray-600 text-lg">
                      Image Align :{" "}
                    </span>
                    <select
                      onChange={(e) => setImageAlignment(e.target.value)}
                      className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                      value={imageAlignment}
                      name=""
                      id=""
                    >
                      <option value="flex-start">Top</option>
                      <option value="center">Center</option>
                      <option value="flex-end">Bottom</option>
                    </select>
                  </div>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="bg-[#d5f6d5] mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-gray-900 flex items-center justify-center gap-4 hover:opacity-90 border-2 border-gray-900"
              >
                {loader ? (
                  <div
                    class={`flex items-center text-gray-900 space-x-2 ${styles.loader}`}
                  >
                    <div class="">Downloading</div>
                    <div class="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div class="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div class="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                  </div>
                ) : (
                  <>
                    Download Pdf <FaDownload className="text-3xl" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="w-full px-4 sm:px-8 mt-12">
          <Steps heading={"How to Convert PNG to PDF?"} steps={steps} />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-20">
          <WhyChooseBolosign
            heading={"Features of BoloSign's PNG to PDF Converter"}
            description={`Our platform is designed with simplicity in mind, making it easy for anyone to convert PNG to PDF without any technical expertise. Also, we prioritize your privacy and data protection by processing and storing your documents securely.`}
            reasons={reasons}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <PricingCards
            heading={"Looking for more than PDF tools?"}
            description={`Join 50,000+ teams who are already signing and accelerating their document workflow with BoloSign.`}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <TryBolosign
            heading={"Try BoloSign Free for 7 Days!"}
            bgColor={"bg-[#d5f6d5]"}
          />
        </div>

        <div className="w-full px-4 sm:px-8 mt-16 sm:mt-32">
          <OtherPdfTools
            heading={`Other PDF Editing Tools`}
            description={`Free online PDF tools to merge, split, rotate, compress, convert, and many more.
Manage your daily PDF tasks with our online PDF tools.`}
          />
        </div>

        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="PNG_TO_PDF"
          />
        )}

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32 flex flex-col gap-12">
          <h2 className="text-center font-bold leading-[45px] sm:leading-[55px] lg:leading-[70px] text-[36px] sm:text-[48px] lg:text-[60px]">
            Frequently Asked Questions
          </h2>
          <div className="max-w-screen-lg mx-auto w-full flex flex-col gap-6">
            {faqData.map((faq, index) => (
              <FaqCard
                key={index}
                faqQuestion={faq.faqQuestion}
                faqAnswer={faq.faqAnswer}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PngToPdf;
